<script setup lang="ts">
  defineOptions({
    name: 'CPPortalSidebarToggle',
  })

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@core/utils/core.utils'

  import { ChevronLeftIcon, ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'

  import CPTooltip from '@ui/components/CPTooltip.vue'

  /** The handle on the content area's left edge that collapses and expands the portal sidebar. */
  defineProps({
    isCollapsed: {
      type: Boolean,
      required: true,
    },
  })

  const emit = defineEmits<{
    toggle: []
  }>()

  const { t } = useI18n()
</script>

<template>
  <CPTooltip placement="right" variant="dark">
    <template #trigger>
      <div
        :id="createElementId('sidebar', 'toggle', 'button')"
        class="sidebar-toggle-btn cursor-pointer"
        :class="{ 'sidebar-collapsed': isCollapsed }"
        role="button"
        tabindex="0"
        :aria-label="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
        :title="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
        @click="emit('toggle')"
        @keydown.enter="emit('toggle')"
        @keydown.space.prevent="emit('toggle')"
      >
        <span class="sidebar-toggle-inner">
          <span class="toggle-line"></span>
          <span class="sidebar-toggle-icons">
            <ChevronLeftIcon v-if="!isCollapsed" class="sidebar-toggle-icon" />
            <ChevronRightIcon v-else class="sidebar-toggle-icon" />
          </span>
        </span>
      </div>
    </template>
    <div class="px-2 py-1 text-sm">
      {{ isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar') }}
    </div>
  </CPTooltip>
</template>

<style scoped>
  .sidebar-toggle-btn {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 30;
    width: 1rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sidebar-toggle-inner {
    position: relative;
    width: 1rem;
    height: 1rem;
  }

  .toggle-line {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.125rem;
    height: 0.75rem;
    background-color: var(--cp-font-700);
    border-radius: 0.03125rem;
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .toggle-line {
    opacity: 0;
  }

  .sidebar-toggle-icons {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icons {
    opacity: 1;
  }

  .sidebar-toggle-icon {
    width: 1rem;
    height: 1rem;
    color: var(--cp-font-700);
    transition: color 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icon {
    color: var(--cp-primary-500);
  }

  .sidebar-toggle-btn:hover {
    border-left: 1px solid var(--cp-border-500);
    box-sizing: border-box;
  }
</style>
